<template>
  <div class="dashboard-container">
    <!-- HEADER SECTION -->
    <b-card class="mb-3 dashboard-header" no-body>
      <b-card-body class="d-flex justify-content-between align-items-center py-2">
        <div>
          <div class="d-flex align-items-center">
            <div class="header-icon-wrapper mr-2">
              <b-icon-shop class="header-icon" />
            </div>
            <div>
              <h5 class="mb-0">STORES REPORT</h5>
              <small class="header-subtitle">Track store performance and sales analytics</small>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-badge variant="light" class="mr-2 date-badge">
            <b-icon-calendar class="mr-1" />{{ formattedDateRange }}
          </b-badge>
          <b-badge variant="primary" pill class="animated-badge">
            {{ filteredItems.length }} {{ filteredItems.length === 1 ? 'Record' : 'Records' }}
          </b-badge>
        </div>
      </b-card-body>
    </b-card>

    <!-- FILTERS CARD -->
    <b-card class="mb-3 filters-card" no-body>
      <b-card-body class="py-2">
        <div class="row align-items-end">
          <div class="col-lg-8">
            <div class="d-flex flex-wrap align-items-end" style="gap: 10px;">
              <div style="min-width: 250px;">
                <label class="small text-muted mb-1">
                  <b-icon-calendar class="mr-1" size="sm" />DATE RANGE
                </label>
                <date-picker
                  v-model="date_range"
                  format="MM-DD-YYYY"
                  valueType="format"
                  class="custom-datepicker"
                  placeholder="Select Date Range"
                  range
                  :class="{ 'datepicker-focused': dateRangeFocused }"
                  @focus="dateRangeFocused = true"
                  @blur="dateRangeFocused = false"
                ></date-picker>
              </div>

              <div class="d-flex flex-wrap" style="gap: 5px;">
                <b-button
                  v-for="btn in quickDateButtons"
                  :key="btn.id"
                  size="sm"
                  :variant="picked_date_range === btn.id ? 'danger' : 'outline-danger'"
                  @click="set_dates(btn.id)"
                  class="quick-date-btn py-1 px-2"
                >
                  <b-icon v-if="picked_date_range === btn.id" :icon="btn.icon" class="mr-1" size="sm" />
                  {{ btn.label }}
                </b-button>
              </div>
            </div>
          </div>
          
          <div class="col-lg-4 mt-2 mt-lg-0">
            <div class="d-flex justify-content-end">
              <b-button
                variant="success"
                @click="fetch_init"
                class="search-btn"
                :disabled="items_being_loaded"
              >
                <b-icon-search class="mr-1" /> 
                <span v-if="!items_being_loaded">SEARCH</span>
                <span v-else>
                  <b-icon-arrow-repeat spin class="mr-1" />LOADING...
                </span>
              </b-button>
            </div>
          </div>
        </div>

        <!-- ADVANCED FILTERS -->
        <div class="mt-2">
          <b-button 
            variant="link" 
            size="sm" 
            @click="showAdvancedFilters = !showAdvancedFilters"
            class="p-0 text-decoration-none"
          >
            <b-icon :icon="showAdvancedFilters ? 'chevron-up' : 'chevron-down'" class="mr-1" size="sm" />
            {{ showAdvancedFilters ? 'Hide' : 'Show' }} Filters
          </b-button>
          
          <b-collapse v-model="showAdvancedFilters">
            <div class="mt-2 pt-2 border-top">
              <div class="row">
                <div class="col-md-3 mb-2">
                  <label class="small text-muted mb-1">
                    <b-icon-file-text class="mr-1" size="sm" />REPORT TYPE
                  </label>
                  <b-form-select
                    :options="details"
                    v-model="details_filter"
                    size="sm"
                    class="filter-select"
                    @change="fetch_init"
                  >
                    <template #first>
                      <b-form-select-option :value="null">SELECT ANY</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div class="col-md-3 mb-2">
                  <label class="small text-muted mb-1">
                    <b-icon-building class="mr-1" size="sm" />LOCATION
                  </label>
                  <b-form-select
                    :options="filteredLocations"
                    v-model="picked_location_id"
                    size="sm"
                    class="filter-select"
                    @change="fetch_init"
                  >
                    <template #first>
                      <b-form-select-option :value="null">All Locations</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div class="col-md-3 mb-2">
                  <label class="small text-muted mb-1">
                    <b-icon-receipt class="mr-1" size="sm" />INVOICE TYPE
                  </label>
                  <b-form-select
                    :options="invoice_options"
                    v-model="picked_invoice_type"
                    size="sm"
                    class="filter-select"
                    @change="fetch_init"
                  >
                    <template #first>
                      <b-form-select-option :value="null">All Types</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div class="col-md-3 mb-2">
                  <label class="small text-muted mb-1">
                    <b-icon-tag class="mr-1" size="sm" />SALE TYPE
                  </label>
                  <b-form-select
                    :options="sale_type_options"
                    v-model="picked_sale_type"
                    size="sm"
                    class="filter-select"
                    @change="fetch_init"
                  >
                    <template #first>
                      <b-form-select-option :value="null">All Sale Types</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>
              </div>
            </div>
          </b-collapse>
        </div>
      </b-card-body>
    </b-card>

    <!-- LOADING STATE -->
    <div v-if="items_being_loaded" class="text-center py-4">
      <b-spinner variant="primary" type="grow" label="Loading..."></b-spinner>
      <p class="mt-2 text-muted small">Fetching store data...</p>
    </div>

    <!-- MAIN CONTENT -->
    <template v-else-if="filteredItems.length > 0">
      <!-- SUMMARY STATS CARDS -->
      <div v-if="details_filter === 1" class="row mb-2">
        <div class="col-xl-3 col-lg-6 col-md-6 mb-2" v-for="stat in summaryStats" :key="stat.label">
          <div class="compact-kpi-card" :style="stat.style">
            <div class="compact-kpi-info" style="width: 100%;">
              <span class="compact-kpi-label">{{ stat.label }}</span>
              <span class="compact-kpi-value">{{ stat.value }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- DATA TABLE SECTION -->
      <b-card no-body class="mt-2 data-table-card">
        <b-card-header class="bg-light d-flex justify-content-between align-items-center py-1">
          <div class="d-flex align-items-center">
            <b-icon-table class="mr-2" size="sm" />
            <h6 class="mb-0">{{ getTableTitle() }}</h6>
            <b-badge variant="secondary" class="ml-2">{{ filteredItems.length }} {{ filteredItems.length === 1 ? 'record' : 'records' }}</b-badge>
          </div>
          <div>
            <b-button v-if="packet.userDetails.userrole == 'admin'" size="sm" variant="outline-secondary" @click="exportToCSV" class="mr-1">
              <b-icon-download class="mr-1" size="sm" /> Export
            </b-button>
            <b-button size="sm" variant="outline-primary" @click="fetch_init">
              <b-icon-arrow-repeat class="mr-1" size="sm" /> Refresh
            </b-button>
          </div>
        </b-card-header>
        <b-card-body class="p-0">
          <b-overlay :show="items_being_loaded" spinner-variant="primary" spinner-type="grow" spinner-small rounded="sm">
            <div class="table-wrapper-sm">
              <table class="compact-table">
                <thead>
                  <tr>
                    <th v-for="col in allowed_cols()" :key="col"
                        :class="getHeaderClass(col)"
                        :style="getHeaderStyle(col)">
                      <div class="d-flex align-items-center justify-content-between">
                        <span>{{ (fixed_columns_dict[col] || col) }}</span>
                        <b-button
                          v-if="isSortableColumn(col)"
                          variant="link"
                          size="sm"
                          class="p-0 ml-1 text-muted sort-btn"
                          @click="sortByColumn(col)"
                        >
                          <b-icon-arrow-up-short v-if="sortColumn === col && sortDirection === 'asc'" size="sm" />
                          <b-icon-arrow-down-short v-else-if="sortColumn === col && sortDirection === 'desc'" size="sm" />
                          <b-icon-arrow-up-short v-else size="sm" class="text-muted" style="opacity: 0.3;" />
                        </b-button>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- DATA ROWS -->
                  <tr v-for="(item, idx) in paginatedItems" :key="idx"
                      :class="getRowClass(item)">
                    <td v-for="row_col in allowed_cols()" :key="row_col"
                        :class="getCellClass(row_col, item[row_col])">
                      <span v-if="row_col == 'locsub'">
                        {{ packet.locsubsdict[item[row_col]] || item[row_col] }}
                      </span>
                      <span v-else-if="['invoice_id', 'lease_ids', 'lease_company'].includes(row_col)">
                        {{ item[row_col] || '—' }}
                      </span>
                      <span v-else-if="row_col.startsWith('customer_')">
                        {{ item[row_col] || '—' }}
                      </span>
                      <span v-else-if="row_col == 'is_delivered'">
                        <b-badge :variant="item[row_col] ? 'success' : 'warning'" pill class="delivery-badge">
                          {{ item[row_col] ? 'Delivered' : 'Not Delivered' }}
                        </b-badge>
                      </span>
                      <span v-else-if="isNumericColumn(row_col)">
                        {{ formatCurrency(item[row_col]) }}
                      </span>
                      <span v-else>
                        {{ item[row_col] !== null && item[row_col] !== undefined ? item[row_col] : '—' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
                <!-- Footer with Totals -->
                <tfoot v-if="showFooter" class="totals-row">
                  <tr>
                    <td v-for="col in allowed_cols()" :key="'footer-' + col"
                        class="fw-bold">
                      <span v-if="col === 'locsub'">TOTAL</span>
                      <span v-else-if="isNumericColumn(col)">
                        {{ formatCurrency(columnTotal(col)) }}
                      </span>
                      <span v-else>—</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="filteredItems.length > perPage" class="pagination-wrapper d-flex justify-content-between align-items-center p-2 bg-light">
              <small class="text-muted">
                Showing {{ ((currentPage - 1) * perPage) + 1 }} to {{ Math.min(currentPage * perPage, filteredItems.length) }} of {{ filteredItems.length }} records
              </small>
              <b-pagination
                v-model="currentPage"
                :total-rows="filteredItems.length"
                :per-page="perPage"
                size="sm"
                class="mb-0"
                align="center"
              ></b-pagination>
              <b-form-select v-model="perPage" size="sm" style="width: 65px;" @change="currentPage = 1">
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </b-form-select>
            </div>
          </b-overlay>
        </b-card-body>
      </b-card>
    </template>

    <!-- EMPTY STATE -->
    <div v-else-if="!items_being_loaded && items.length === 0" class="text-center py-4">
      <b-alert show variant="light" class="mb-0">
        <b-icon-inbox class="mr-2" />
        No records found. Select a date range and click SEARCH.
      </b-alert>
    </div>

    <div v-else-if="!items_being_loaded && filteredItems.length === 0 && items.length > 0" class="text-center py-4">
      <b-alert show variant="info" class="mb-0">
        <b-icon-filter-circle class="mr-2" />
        No data matches the selected filters. Try adjusting your filters.
      </b-alert>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import 'moment/locale/en-gb';
import global_vars from '@/utilities/global_vars';
import Backer from '@/components/globals/Backer';
import Requestor from '@/utilities/Requestor';
import JsonCSV from 'vue-json-csv';

import {
  BIconShop,
  BIconCalendar,
  BIconSearch,
  BIconTable,
  BIconArrowRepeat,
  BIconDownload,
  BIconBuilding,
  BIconTag,
  BIconInbox,
  BIconFileText,
  BIconReceipt,
  BIconArrowUpShort,
  BIconArrowDownShort,
  BIconFilterCircle,
  BIconChevronUp,
  BIconChevronDown,
  BIconPerson
} from 'bootstrap-vue';

moment.locale('en-gb');

export default {
  name: "ReportsStoresSale",
  components: {
    Backer,
    DatePicker,
    JsonCSV,
    BIconShop,
    BIconCalendar,
    BIconSearch,
    BIconTable,
    BIconArrowRepeat,
    BIconDownload,
    BIconBuilding,
    BIconTag,
    BIconInbox,
    BIconFileText,
    BIconReceipt,
    BIconArrowUpShort,
    BIconArrowDownShort,
    BIconFilterCircle,
    BIconChevronUp,
    BIconChevronDown,
    BIconPerson
  },
  data: function() {
    return {
      packet: {},
      items: [],
      items_being_loaded: false,
      global_vars: global_vars,
      date_range: [moment().startOf('month').format("MM-DD-YYYY"), moment().format("MM-DD-YYYY")],
      picked_date_range: 'this_month',
      dateRangeFocused: false,
      showAdvancedFilters: false,
      
      sale_type_options: [
        { value: null, text: "ALL SALE TYPES" },
        { value: 'regular', text: "REGULAR SALES" },
        { value: 'layaway', text: "LAYAWAY SALES" }
      ],
      picked_sale_type: null,
      details: [
        { value: 1, text: "SUMMARY" },
        { value: 2, text: "DETAILED REFUNDS" },
        { value: 3, text: "DETAILED SALES" },
        { value: 4, text: "PER INVOICE SALES" },
      ],
      invoice_options: [
        { value: null, text: "ALL" },
        { value: 1, text: "WRITTEN" },
        { value: 2, text: "DELIVERED" },
        { value: 3, text: "NOT DELIVERED" },
      ],
      
      fixed_columns_dict: {
        locsub: "LOCATION",
        total_without_tax: "TOTAL SALE",
        total_refunded_without_tax: "TOTAL REFUNDED",
        total_sales_tax: "SLS TAX",
        total_refunded_st: "SLS TAX REFUNDED",
        total_cc_sales: "CC SALES",
        total_finance_sales: "FIN SALES",
        total_tax_exempt_sales: "TAX EXEMPT SALES",
        total_cash_sales: "CASH SALES",
        total_cc_refunds: "CC REFUNDS",
        total_finance_refunds: "FIN REFUNDS",
        total_tax_exempt_refunds: "TAX EXEMPT REFUNDS",
        total_cash_refunds: "CASH REFUNDS",
        invoice_id: "INVOICE #",
        lease_ids: "LEASE IDS",
        lease_company: "LEASE COMPANY",
        customer_fullname: "CUSTOMER NAME",
        customer_email: "EMAIL",
        customer_phone: "PHONE",
        customer_phone_2: "PHONE 2",
        customer_address_line_1: "ADDRESS LINE 1",
        customer_address_line_2: "ADDRESS LINE 2",
        customer_city: "CITY",
        customer_state: "STATE",
        customer_zipcode: "ZIPCODE",
        is_delivered: "DELIVERY STATUS"
      },
      columns: [],
      picked_location_id: null,
      details_filter: 1,
      picked_invoice_type: null,
      currentPage: 1,
      perPage: 25,
      sortColumn: null,
      sortDirection: 'asc',
      
      quickDateButtons: [
        { id: 'today', label: 'Today', icon: 'calendar-day' },
        { id: 'yesterday', label: 'Yesterday', icon: 'calendar-day' },
        { id: 'this_week', label: 'This Week', icon: 'calendar-week' },
        { id: 'last_week', label: 'Last Week', icon: 'calendar-week' },
        { id: 'this_month', label: 'This Month', icon: 'calendar-month' },
        { id: 'last_month', label: 'Last Month', icon: 'calendar-month' }
      ],
    }
  },
  computed: {
    formattedDateRange() {
      if (this.date_range && this.date_range.length === 2) {
        return `${this.date_range[0]} - ${this.date_range[1]}`;
      }
      return 'Select dates';
    },
    
    filteredLocations() {
      if (!this.packet.locations) return [];
      const excludedLocationNames = [
        'FOLCROFT WH',
        '101 WH',
        '9TH ST WH',
        'OUT OF STOCK',
        'MECCA SHIPPING',
        'ONLINE SALES'
      ];
      return this.packet.locations.filter(location =>
        !excludedLocationNames.includes(location.text)
      );
    },
    
    filteredItems() {
      let filtered = this.items.filter(item => this.allowed_row(item));
      
      if (this.sortColumn) {
        filtered = filtered.sort((a, b) => {
          let aVal = a[this.sortColumn];
          let bVal = b[this.sortColumn];

          if (this.isNumericColumn(this.sortColumn)) {
            aVal = parseFloat(aVal || 0);
            bVal = parseFloat(bVal || 0);
          }

          if (this.sortColumn === 'locsub') {
            aVal = this.packet.locsubsdict[aVal] || aVal;
            bVal = this.packet.locsubsdict[bVal] || bVal;
          }

          if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
          if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      }
      
      return filtered;
    },
    
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredItems.slice(start, end);
    },
    
    numericColumns() {
      return ['total_without_tax', 'total_refunded_without_tax', 'total_sales_tax',
             'total_refunded_st', 'total_cc_sales', 'total_finance_sales',
             'total_tax_exempt_sales', 'total_cash_sales', 'total_cc_refunds',
             'total_finance_refunds', 'total_tax_exempt_refunds', 'total_cash_refunds'];
    },
    
    summaryStats() {
      if (this.filteredItems.length === 0 || this.details_filter !== 1) return [];

      const totals = this.filteredItems.reduce((acc, item) => {
        acc.totalSales += parseFloat(item.total_without_tax || 0);
        acc.totalRefunds += parseFloat(item.total_refunded_without_tax || 0);
        acc.netAmount += (parseFloat(item.total_without_tax || 0) - parseFloat(item.total_refunded_without_tax || 0));
        acc.salesTax += parseFloat(item.total_sales_tax || 0);
        return acc;
      }, { totalSales: 0, totalRefunds: 0, netAmount: 0, salesTax: 0 });

      return [
        {
          label: 'Total Sales',
          value: this.formatCurrency(totals.totalSales),
          style: 'border-left: 4px solid #10b981;'
        },
        {
          label: 'Total Refunds',
          value: this.formatCurrency(totals.totalRefunds),
          style: 'border-left: 4px solid #ef4444;'
        },
        {
          label: 'Net Amount',
          value: this.formatCurrency(totals.netAmount),
          style: 'border-left: 4px solid #6366f1;'
        },
        {
          label: 'Sales Tax',
          value: this.formatCurrency(totals.salesTax),
          style: 'border-left: 4px solid #f59e0b;'
        }
      ];
    },
    
    showFooter() {
      return this.details_filter !== 4 && this.filteredItems.length > 0;
    }
  },
  watch: {
    filteredItems() {
      this.currentPage = 1;
    }
  },
  methods: {
    formatCurrency(value) {
      const num = parseFloat(value || 0);
      if (isNaN(num)) return '$0.00';
      return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    
    allowed_cols() {
      if (!this.columns.length) return [];
      
      let cols = [];
      switch (this.details_filter) {
        case 1:
          cols = ['locsub', 'total_without_tax', 'total_refunded_without_tax',
                 'total_sales_tax', 'total_refunded_st', 'total_cash_sales',
                 'total_cc_sales', 'total_finance_sales', 'total_tax_exempt_sales'];
          break;
        case 2:
          cols = ['locsub', 'total_without_tax', 'total_refunded_without_tax',
                 'total_sales_tax', 'total_refunded_st'];
          cols = cols.concat(this.columns.filter(col =>
            col.startsWith('total_') && col.endsWith('_refunds')
          ));
          cols = cols.concat(this.columns.filter(col =>
            col.startsWith('REFUNDS (')
          ));
          break;
        case 3:
          cols = ['locsub', 'total_without_tax', 'total_refunded_without_tax',
                 'total_sales_tax', 'total_refunded_st'];
          cols = cols.concat(this.columns.filter(col =>
            col.startsWith('total_') && col.endsWith('_sales')
          ));
          cols = cols.concat(this.columns.filter(col =>
            col.startsWith('SALES (')
          ));
          break;
        case 4:
          cols = [
            'invoice_id', 'customer_fullname', 'is_delivered', 'lease_ids',
            'lease_company', 'locsub', 'total_without_tax',
            'total_refunded_without_tax', 'total_sales_tax', 'total_refunded_st',
            'total_cash_sales', 'total_cc_sales', 'total_finance_sales',
            'total_tax_exempt_sales'
          ];
          cols = cols.concat(this.columns.filter(col =>
            col.startsWith('SALES (')
          ));
          const additionalCustomerColumns = [
            'customer_email', 'customer_phone', 'customer_phone_2',
            'customer_address_line_1', 'customer_address_line_2',
            'customer_city', 'customer_state', 'customer_zipcode'
          ];
          cols = cols.concat(additionalCustomerColumns.filter(col => this.columns.includes(col)));
          break;
        default:
          cols = ['locsub', 'total_without_tax', 'total_refunded_without_tax'];
      }
      
      return cols.filter(col => this.columns.includes(col));
    },
    
    allowed_row(item) {
      if (this.picked_location_id != null) {
        return item.location_id == this.picked_location_id;
      }
      return true;
    },
    
    set_dates(value) {
      let start = null;
      let end = null;

      switch(value) {
        case 'today':
          start = moment().format("MM-DD-YYYY");
          end = moment().format("MM-DD-YYYY");
          break;
        case 'yesterday':
          start = moment().add(-1, 'days').format("MM-DD-YYYY");
          end = moment().add(-1, 'days').format("MM-DD-YYYY");
          break;
        case 'this_week':
          start = moment().startOf('week').format("MM-DD-YYYY");
          end = moment().endOf('week').format("MM-DD-YYYY");
          break;
        case 'last_week':
          start = moment().subtract(1, 'weeks').startOf('week').format("MM-DD-YYYY");
          end = moment().subtract(1, 'weeks').endOf('week').format("MM-DD-YYYY");
          break;
        case 'this_month':
          start = moment().startOf('month').format("MM-DD-YYYY");
          end = moment().endOf('month').format("MM-DD-YYYY");
          break;
        case 'last_month':
          start = moment().subtract(1, 'months').startOf('month').format("MM-DD-YYYY");
          end = moment().subtract(1, 'months').endOf('month').format("MM-DD-YYYY");
          break;
      }

      if (start && end) {
        this.date_range = [start, end];
        this.picked_date_range = value;
        this.fetch_init();
      }
    },
    
    fetch_init() {
      if (!this.details_filter) {
        this.$bvToast.toast('Please select a report type.', {
          title: 'Invalid Filter',
          variant: 'warning',
          solid: true
        });
        return;
      }

      if (!this.date_range || this.date_range.length !== 2) {
        this.$bvToast.toast('Please select a date range.', {
          title: 'Invalid Date Range',
          variant: 'warning',
          solid: true
        });
        return;
      }

      let location_id = this.packet.userDetails.properties.location_id;
      if (this.packet.userDetails.userrole == 'admin') {
        location_id = null;
      }

      let that = this;
      let data = {
        dates: this.date_range,
        location_id: location_id,
        picked_invoice_type: this.picked_invoice_type,
        details_filter: this.details_filter,
        sale_type: this.picked_sale_type
      };
      
      let path = "/api/reports/reports_stores_sale/";
      
      let s = function(response) {
        if (response.data.result) {
          that.items = response.data.result;
          that.derive_sorted_columns();
          that.$bvToast.toast(
            `Loaded ${that.items.length} records`,
            { title: "Success", autoHideDelay: 1500, variant: "success" }
          );
        } else {
          that.$bvToast.toast(
            response.data.error || "Unknown error occurred",
            { title: "Error", autoHideDelay: 1500, variant: "warning" }
          );
        }
        that.items_being_loaded = false;
      }

      let f = function(error) {
        console.error('API Error:', error);
        that.$bvToast.toast(
          "Error occurred. Please try again.",
          { title: "Error", autoHideDelay: 1500, variant: "danger" }
        );
        that.items_being_loaded = false;
      }
      
      this.items_being_loaded = true;
      this.items = [];
      Requestor.postSyncP(path, data, this, s, f);
    },
    
    derive_sorted_columns() {
      if (this.items.length > 0) {
        let dcs = Object.keys(this.items[0]);
        
        let columns = ["locsub", "total_without_tax", "total_refunded_without_tax",
                     "total_sales_tax", "total_refunded_st"];
        
        let salesColumns = dcs.filter(col =>
          col.startsWith('total_') && col.endsWith('_sales')
        );
        columns = columns.concat(salesColumns);
        
        let refundColumns = dcs.filter(col =>
          col.startsWith('total_') && col.endsWith('_refunds')
        );
        columns = columns.concat(refundColumns);
        
        let financeSales = dcs.filter(col => col.startsWith('SALES ('));
        columns = columns.concat(financeSales);
        
        let financeRefunds = dcs.filter(col => col.startsWith('REFUNDS ('));
        columns = columns.concat(financeRefunds);
        
        let invoiceColumns = ['invoice_id', 'lease_ids', 'lease_company', 'is_delivered'];
        columns = columns.concat(invoiceColumns.filter(col => dcs.includes(col)));
        
        let customerColumns = dcs.filter(col => col.startsWith('customer_'));
        columns = columns.concat(customerColumns);
        
        this.columns = columns;
      } else {
        this.columns = [];
      }
    },
    
    sortByColumn(col) {
      if (this.sortColumn === col) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = col;
        this.sortDirection = 'asc';
      }
    },
    
    isSortableColumn(col) {
      return !['invoice_id', 'lease_ids', 'lease_company'].includes(col);
    },
    
    exportToCSV() {
      if (this.filteredItems.length === 0) {
        this.$bvToast.toast('No data to export', { title: 'Info', variant: 'info' });
        return;
      }
      
      const headers = this.allowed_cols().map(col => this.fixed_columns_dict[col] || col);
      const rows = this.filteredItems.map(item => {
        return this.allowed_cols().map(col => {
          if (col === 'locsub') {
            return this.packet.locsubsdict[item[col]] || item[col] || '';
          } else if (col === 'is_delivered') {
            return item[col] ? 'Delivered' : 'Not Delivered';
          } else if (this.isNumericColumn(col)) {
            const num = item[col] != null ? parseFloat(item[col]) : 0;
            return num.toFixed(2);
          } else {
            return item[col] || '';
          }
        });
      });
      
      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.getTableTitle().toUpperCase().replace(/\s/g, '_')}_${moment().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      this.$bvToast.toast('Export completed!', { title: 'Success', autoHideDelay: 1500, variant: 'success' });
    },
    
    getTableTitle() {
      const titles = {
        1: 'Sales Summary',
        2: 'Detailed Refunds',
        3: 'Detailed Sales',
        4: 'Per Invoice Sales'
      };
      return titles[this.details_filter] || 'Store Reports';
    },
    
    getHeaderClass(col) {
      return this.isNumericColumn(col) ? 'text-right' : 'text-center';
    },
    
    getHeaderStyle(col) {
      if (col === 'locsub') return 'min-width: 180px;';
      if (['invoice_id', 'lease_ids', 'lease_company'].includes(col)) return 'min-width: 120px;';
      if (col.startsWith('customer_')) return 'min-width: 150px;';
      if (col === 'is_delivered') return 'min-width: 120px;';
      return 'min-width: 100px;';
    },
    
    getCellClass(col, value) {
      let classes = [];
      if (this.isNumericColumn(col)) classes.push('text-right');
      if (col.includes('refund') && parseFloat(value) > 0) classes.push('text-danger');
      if (col.includes('sales') && parseFloat(value) > 0) classes.push('text-success');
      if (col.includes('tax_exempt') && parseFloat(value) > 0) classes.push('text-tax-exempt');
      if (col === 'is_delivered') classes.push('text-center');
      return classes.join(' ');
    },
    
    getRowClass(item) {
      const netAmount = parseFloat(item.total_without_tax || 0) - parseFloat(item.total_refunded_without_tax || 0);
      if (netAmount < 0) return 'table-danger-row';
      if (netAmount > 10000) return 'table-success-row';
      return '';
    },
    
    isNumericColumn(col) {
      const nonNumericCols = ['locsub', 'invoice_id', 'lease_ids', 'lease_company', 'is_delivered'];
      if (col.startsWith('customer_')) return false;
      return !nonNumericCols.includes(col);
    },
    
    columnTotal(col) {
      if (!this.isNumericColumn(col)) return 0;
      return this.filteredItems.reduce((sum, item) => {
        const value = parseFloat(item[col] || 0);
        return isNaN(value) ? sum : sum + value;
      }, 0);
    }
  },
  created() {
    this.packet = JSON.parse(localStorage.packet || '{}');
    this.packet.userDetails = JSON.parse(localStorage.userDetails || '{}');
    this.$nextTick(() => {
      this.fetch_init();
    });
  }
}
</script>

<style scoped>
.dashboard-container {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
  padding: 12px;
}

/* Header Styles */
.dashboard-header {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: white;
  border: none;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.header-icon-wrapper {
  width: 36px;
  height: 36px;
  background: rgba(255,255,255,0.15);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon { font-size: 18px; animation: pulse 2s infinite; }
@keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }

.dashboard-header h5 {
  color: white;
  font-weight: 600;
}

.header-subtitle {
  color: rgba(255,255,255,0.8);
}

.date-badge {
  background-color: rgba(255,255,255,0.15);
  color: white;
  border: none;
  padding: 2px 10px;
  border-radius: 14px;
  font-size: 11px;
}

.animated-badge {
  animation: fadeIn 0.5s ease-out;
}

/* Filters Card */
.filters-card {
  border: none;
  border-radius: 14px;
  box-shadow: 0 1px 5px rgba(0,0,0,0.05);
  background: white;
}

.custom-datepicker {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 12px;
  width: 100%;
}

.datepicker-focused {
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99,102,241,0.1);
}

.quick-date-btn {
  border-radius: 14px;
  font-size: 11px;
  padding: 3px 8px;
}

.filter-select {
  border-radius: 7px;
  background: #f8fafc;
  border-color: #e2e8f0;
  font-size: 12px;
}

.search-btn {
  border-radius: 8px;
  padding: 4px 20px;
  font-weight: 600;
  font-size: 12px;
}

/* Compact KPI Cards */
.compact-kpi-card {
  background: white;
  border-radius: 10px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  transition: all 0.2s;
}

.compact-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
}

.compact-kpi-label {
  font-size: 10px;
  color: #64748b;
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.compact-kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  display: block;
}

/* Data Table */
.data-table-card {
  border: none;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  overflow: hidden;
}

.table-wrapper-sm {
  overflow-x: auto;
}

.compact-table {
  width: 100%;
  font-size: 11px;
  border-collapse: collapse;
}

.compact-table th,
.compact-table td {
  padding: 6px 8px;
  border: 1px solid #dee2e6;
}

.compact-table th {
  background: #c3cdd3;
  font-weight: 600;
  position: sticky;
  top: 0;
  white-space: nowrap;
}

.compact-table td {
  white-space: nowrap;
}

.compact-table tbody tr:hover {
  background: #e0e7ff !important;
}

.table-success-row {
  background: linear-gradient(90deg, rgba(16, 185, 129, 0.05), transparent);
}

.table-danger-row {
  background: linear-gradient(90deg, rgba(239, 68, 68, 0.05), transparent);
}

.totals-row {
  background: #fef3c7;
  font-weight: 600;
}

.totals-row td {
  background: #fef3c7;
}

.text-right {
  text-align: right !important;
}

.text-center {
  text-align: center !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-success {
  color: #28a745 !important;
}

.text-tax-exempt {
  color: #6f42c1 !important;
}

.delivery-badge {
  font-size: 9px;
  padding: 2px 8px;
}

.sort-btn {
  text-decoration: none;
}

.sort-btn:hover {
  text-decoration: none;
}

.pagination-wrapper {
  border-top: 1px solid #e2e8f0;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.compact-kpi-card, .data-table-card, .filters-card {
  animation: fadeIn 0.5s ease-out;
}

.border-top {
  border-top: 1px solid #e2e8f0;
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard-container { padding: 8px; }
  .compact-kpi-value { font-size: 1.1rem; }
  .compact-table th, .compact-table td { padding: 4px 6px; font-size: 9px; }
  .dashboard-header h5 { font-size: 14px; }
  .header-subtitle { font-size: 10px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/ReportsStoresSale.vue